import { metersToPixels } from '../constants.ts';
import { add, dot, fromAngle, length, lengthSq, normalize, scale, sub } from '../math.ts';
import type { Vec2 } from '../types.ts';

/** Velocity-change axes, in world pixels. Equal values are the planning disk. */
export type ReachAxes = {
  turn: number;
  accel: number;
  brake: number;
};

/** Sample speed for the workshop's second oval, so the coast point sits ahead of the hull. */
export const REACH_GHOST_SPEED_M = 15;

/** True when the oval is a disk, so the circle painters and rim cache still apply. */
export function reachIsDisk(axes: ReachAxes): boolean {
  return axes.turn === axes.accel && axes.accel === axes.brake;
}

export function reachAxesFromMeters(turnM: number, accelM: number, brakeM: number): ReachAxes {
  return {
    turn: metersToPixels(turnM),
    accel: metersToPixels(accelM),
    brake: metersToPixels(brakeM),
  };
}

/**
 * True when `point` lies in the heading-aligned oval around `center`.
 * Ahead of the centre uses `accel`; behind it uses `brake`; across uses `turn`.
 * Equal axes match a disk of that radius.
 */
export function pointInReach(
  center: Vec2,
  heading: number,
  point: Vec2,
  axes: ReachAxes,
): boolean {
  if (axes.turn <= 0) {
    return false;
  }

  const forward = fromAngle(heading, 1);
  const right = fromAngle(heading + Math.PI / 2, 1);
  const offset = { x: point.x - center.x, y: point.y - center.y };
  const along = dot(offset, forward);
  const across = dot(offset, right);
  // `>` so a zero forward reach still contains the coast point (it sits on the brake side).
  const axial = along > 0 ? axes.accel : axes.brake;

  if (axial <= 0) {
    return false;
  }

  return (along * along) / (axial * axial) + (across * across) / (axes.turn * axes.turn) <= 1;
}

/** Local outline: x across the bow, y ahead of the coast point. Closed, last point repeats the first. */
export function reachOutline(axes: ReachAxes, steps = 64): Vec2[] {
  const count = Math.max(8, steps);
  const points: Vec2[] = [];

  for (let index = 0; index <= count; index += 1) {
    const theta = (index / count) * Math.PI * 2;
    const alongDir = Math.cos(theta);
    const axial = alongDir >= 0 ? axes.accel : axes.brake;

    points.push({
      x: Math.sin(theta) * axes.turn,
      y: alongDir * axial,
    });
  }

  return points;
}

/** World-space outline of the oval, rotated onto `heading`. */
export function reachWorldPoints(
  center: Vec2,
  heading: number,
  axes: ReachAxes,
  steps = 64,
): Vec2[] {
  const forward = fromAngle(heading, 1);
  const right = fromAngle(heading + Math.PI / 2, 1);

  return reachOutline(axes, steps).map((point) => ({
    x: center.x + right.x * point.x + forward.x * point.y,
    y: center.y + right.y * point.x + forward.y * point.y,
  }));
}

const REACH_RIM_INSET = 1e-3;

/**
 * Sticky aim for a heading-aligned oval. Inside points pass through. Points in the
 * halo project onto the outline, seated a hair toward the centre.
 */
export function stickToReach(
  point: Vec2,
  center: Vec2,
  heading: number,
  axes: ReachAxes,
  opts: { enabled: boolean; overflowWorld: number },
): Vec2 | null {
  if (pointInReach(center, heading, point, axes)) {
    return point;
  }

  if (!opts.enabled) {
    return null;
  }

  const outline = reachWorldPoints(center, heading, axes, 96);
  let best = outline[0];
  let bestDist = Infinity;

  for (const sample of outline) {
    if (!sample) {
      continue;
    }

    const dist = length(sub(point, sample));

    if (dist < bestDist) {
      best = sample;
      bestDist = dist;
    }
  }

  if (!best || bestDist > opts.overflowWorld) {
    return null;
  }

  const inward = sub(center, best);
  const seated =
    lengthSq(inward) > 1e-8
      ? add(best, scale(normalize(inward), REACH_RIM_INSET))
      : best;

  return pointInReach(center, heading, seated, axes) ? seated : best;
}

/** Disk test used to prove equal axes match `MOVEMENT_RADIUS`. */
export function inDisk(point: Vec2, radius: number): boolean {
  return lengthSq(point) <= radius * radius;
}
